import type { ArticleType } from "@acme/types";
import { getRouteApi } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArticleCard } from "~/components/blog/article-card";
import { SegmentedFilter } from "~/components/inside/segmented-filter";
import { getBlogCategories } from "~/lib/constants/blog-categories";
import { plural } from "~/lib/plural";
import * as m from "~/paraglide/messages.js";

const routeApi = getRouteApi("/(public)/blog/");

// Mirrors the route's `validateSearch`. Annotated explicitly because the
// compiler drops the contextual type on `navigate`'s search updater here,
// which would silently widen `prev` to `any`.
interface BlogSearch {
  category?: string;
}

interface FilteredArticlesProps {
  articles: ArticleType[];
}

/**
 * The listing: a filter over the subjects something is actually filed under,
 * then the newest article at full width and the rest in a grid.
 *
 * Filtering is conditional rendering rather than a `hidden` toggle, so the
 * server renders every article and a crawler sees the whole listing.
 */
export function FilteredArticles({
  articles,
}: Readonly<FilteredArticlesProps>) {
  const { category } = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  // A filter with one option filters nothing, so it only appears once more than
  // one subject is actually in use.
  const categories = useMemo(() => {
    const present = new Set<string>(articles.map((a) => a.category));
    const inUse = getBlogCategories().filter((o) => present.has(o.value));
    return inUse.length > 1 ? inUse : [];
  }, [articles]);

  // A category only filters while the control that clears it is on screen and
  // the value is one the listing actually offers. Otherwise a stale or hand-
  // typed `?category=` leaves the reader on an empty listing with nothing to
  // press to get back.
  const active = useMemo(
    () =>
      category && categories.some((option) => option.value === category)
        ? category
        : undefined,
    [categories, category]
  );

  const shown = useMemo(
    () => (active ? articles.filter((a) => a.category === active) : articles),
    [articles, active]
  );

  const selectCategory = (next: string) => {
    navigate({
      replace: true,
      resetScroll: false,
      search: (prev: BlogSearch) => ({
        ...prev,
        category: next === "all" ? undefined : next,
      }),
    });
  };

  const [lead, ...rest] = shown;

  return (
    <section aria-label={m.blog_filter_label()}>
      {categories.length > 0 ? (
        <div className="collection-toolbar">
          <SegmentedFilter
            label={m.blog_filter_label()}
            onChange={selectCategory}
            options={[
              { label: m.blog_filter_all(), value: "all" },
              ...categories.map((option) => ({
                label: option.label,
                value: option.value as string,
              })),
            ]}
            value={active ?? "all"}
          />
          <p aria-live="polite" className="filter-count" role="status">
            {plural(shown.length, {
              one: m.blog_count_one,
              other: m.blog_count_other,
              zero: m.blog_count_zero,
            })}
          </p>
        </div>
      ) : null}

      {lead ? <ArticleCard article={lead} variant="feature" /> : null}

      {rest.length > 0 ? (
        <div className="editorial-list">
          <div className="post-grid">
            {rest.map((article) => (
              <ArticleCard article={article} key={article.slug} />
            ))}
          </div>
        </div>
      ) : null}

      {shown.length === 0 ? (
        <div className="archive-empty">
          <h2>{m.blog_empty_title()}</h2>
          <p>{m.blog_empty_description()}</p>
        </div>
      ) : null}
    </section>
  );
}
